// C4 — Importar programación del cliente desde Excel (plana, con columna Área OPCIONAL).
//
// Alpina programa por SKU + cantidad. El ÁREA es opcional en la plantilla:
//   - Si la ponen (nombre completo o código) → manda esa.
//   - Si la dejan vacía → el sistema la deriva del SKU (skus_lineas → área de la
//     línea): auto si corre en 1 sola área, picker si varias/ninguna.
//   - Si el texto no se reconoce / es ambiguo entre plantas → picker en la UI.
// La fila queda marcada hasta resolver el área; recién ahí se puede cargar.
// El programa se mide por área (WS-4).
//
// Parseo + mapeo en el FE (SheetJS) → POST /api/coordinador/programaciones
// (origen=excel_alpina). El backend revalida fechas/scope/SKUs.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import type { CoordinadorProgramacionProduccionSemana } from 'schemas';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { useCrearProgramacion } from '../../lib/api/queries';
import { ApiError } from '../../lib/api/client';
import { parseFechaISO } from './excelFecha';
import { avisoTotalSospechoso, vigenteComparable } from './c4AvisoTotales';

type Data = CoordinadorProgramacionProduccionSemana;

const DOW = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
function fmtDiaCorto(iso: string): string {
  if (!iso || iso.length < 10) return iso || '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(Date.UTC(y, m - 1, d));
  return `${DOW[date.getUTCDay()]} ${d}/${String(m).padStart(2, '0')}`;
}

interface FilaParseada {
  idx: number; // índice interno (key + para los picks de área)
  filaExcel: number; // nro de fila en el Excel (para mensajes)
  fecha: string;
  skuCodigo: string;
  cantidad: number | null;
  cantidadKg: number | null;
  sku_id: string | null;
  candidatas: string[]; // area_ids donde corre el SKU (en scope)
  areaExcel: string | null; // area_id resuelta desde la columna Área del Excel (manda)
  areaTexto: string; // texto crudo de la columna Área (para mensajes / hint)
  autoArea: string | null; // si candidatas.length === 1
  errorBase: string | null; // error que NO depende de la elección de área
}

export function C4ImportarExcelDialog({ data, onClose }: { data: Data; onClose: () => void }) {
  const clientesOrdenados = useMemo(
    () => [...data.clientes].sort((a, b) => Number(b.es_principal) - Number(a.es_principal)),
    [data.clientes],
  );
  const [clienteId, setClienteId] = useState(clientesOrdenados[0]?.id ?? '');
  const [filas, setFilas] = useState<FilaParseada[]>([]);
  // Área elegida manualmente por fila (idx → area_id), para las ambiguas/sin config.
  const [picks, setPicks] = useState<Record<number, string>>({});
  const [nombreArchivo, setNombreArchivo] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const crear = useCrearProgramacion();

  const skusDelCliente = useMemo(
    () => data.skus.filter((s) => s.cliente_id === clienteId),
    [data.skus, clienteId],
  );

  const norm = (s: string) => s.trim().toLowerCase();
  const skuPorCodigo = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of skusDelCliente) m.set(norm(s.codigo_cliente), s.id);
    return m;
  }, [skusDelCliente]);
  const skuDesc = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of skusDelCliente) m.set(norm(s.codigo_cliente), s.descripcion);
    return m;
  }, [skusDelCliente]);
  // SKU → áreas configuradas (en scope), derivadas de skus_lineas → lineas.area_id.
  const areasDeSku = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const sa of data.skus_areas) {
      const arr = m.get(sa.sku_id) ?? [];
      if (!arr.includes(sa.area_id)) arr.push(sa.area_id);
      m.set(sa.sku_id, arr);
    }
    return m;
  }, [data.skus_areas]);
  const areaLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of data.areas) m.set(a.id, `${a.codigo} · ${a.nombre}`);
    return m;
  }, [data.areas]);
  // Texto del Excel (nombre completo o código, normalizado) → area_id. Valor null
  // = ambiguo (mismo nombre en >1 planta) → cae al picker. Lo que ponen en el
  // Excel manda sobre la derivación automática del SKU.
  const areaPorTexto = useMemo(() => {
    const m = new Map<string, string | null>();
    const add = (key: string, id: string) => {
      if (!key) return;
      if (m.has(key) && m.get(key) !== id) m.set(key, null);
      else m.set(key, id);
    };
    for (const a of data.areas) {
      add(norm(a.nombre), a.id);
      add(norm(a.codigo), a.id);
    }
    return m;
  }, [data.areas]);

  function descargarPlantilla() {
    const cliente = data.clientes.find((c) => c.id === clienteId);
    const wb = XLSX.utils.book_new();
    const header = [
      'Fecha (YYYY-MM-DD)',
      'SKU (codigo cliente)',
      'Área (opcional)',
      'Cant. paquetes',
      'Cant. kg (opcional)',
      'Observaciones (opcional)',
    ];
    const ejemplo = [
      data.dias[0],
      skusDelCliente[0]?.codigo_cliente ?? '10148',
      data.areas[0]?.nombre ?? 'Verticales',
      500,
      '',
      'ejemplo — borrar',
    ];
    const ws = XLSX.utils.aoa_to_sheet([header, ejemplo]);
    XLSX.utils.book_append_sheet(wb, ws, 'Produccion');
    // Referencia: SKUs del cliente. El área es OPCIONAL: si la dejás vacía, el
    // sistema la deriva del SKU; si la ponés (nombre o código), manda esa.
    const refSkus = [
      ['SKUS VÁLIDOS DE ' + (cliente?.nombre ?? ''), ''],
      ['Código', 'Descripción'],
      ...skusDelCliente.map((s) => [s.codigo_cliente, s.descripcion]),
    ];
    const wsRef = XLSX.utils.aoa_to_sheet(refSkus);
    XLSX.utils.book_append_sheet(wb, wsRef, 'Referencia');
    // Áreas válidas: el coordinador escribe el nombre completo o el código en la
    // columna "Área (opcional)". Si no la pone, el sistema la deriva del SKU.
    const refAreas = [
      ['ÁREAS VÁLIDAS — escriba el nombre o el código en la columna "Área (opcional)"'],
      ['Código', 'Área', 'Planta'],
      ...data.areas.map((a) => [a.codigo, a.nombre, a.planta_nombre]),
    ];
    const wsAreas = XLSX.utils.aoa_to_sheet(refAreas);
    XLSX.utils.book_append_sheet(wb, wsAreas, 'Áreas');
    XLSX.writeFile(wb, `programacion-${cliente?.nombre ?? 'cliente'}-${data.semana_iso}.xlsx`);
  }

  function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setNombreArchivo(file.name);
    setPicks({});
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'array' });
        const ws = wb.Sheets['Produccion'] ?? wb.Sheets[wb.SheetNames[0]!];
        const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws!, { header: 1, raw: false, defval: '' });
        // Segunda pasada cruda para tomar el serial de la fecha (ver excelFecha).
        const aoaRaw = XLSX.utils.sheet_to_json<unknown[]>(ws!, {
          header: 1,
          raw: true,
          defval: '',
        });
        const diasSet = new Set(data.dias);
        const out: FilaParseada[] = [];
        for (let i = 1; i < aoa.length; i++) {
          const r = aoa[i]!;
          // Columnas: Fecha(0) · SKU(1) · Área opcional(2) · Cant(3) · Kg(4) · Obs(5)
          const fecha = parseFechaISO(aoaRaw[i]?.[0] ?? r[0]);
          const skuCodigo = String(r[1] ?? '').trim();
          const areaTexto = String(r[2] ?? '').trim();
          const cantRaw = String(r[3] ?? '').trim();
          const kgRaw = String(r[4] ?? '').trim();
          if (!fecha && !skuCodigo && !cantRaw && !areaTexto) continue;
          const sku_id = skuPorCodigo.get(norm(skuCodigo)) ?? null;
          const candidatas = sku_id ? (areasDeSku.get(sku_id) ?? []) : [];
          // Área del Excel (manda): nombre o código. undefined (no existe) y null
          // (ambiguo) → null → cae a la derivación del SKU / picker.
          const areaExcel = areaTexto ? (areaPorTexto.get(norm(areaTexto)) ?? null) : null;
          const cantidad =
            cantRaw === '' ? null : Number(cantRaw.replace(/\./g, '').replace(',', '.'));
          const cantidadKg = kgRaw === '' ? null : Number(kgRaw.replace(',', '.'));
          let errorBase: string | null = null;
          if (!diasSet.has(fecha)) errorBase = `Fecha "${fecha}" fuera de la semana`;
          else if (!sku_id) errorBase = `SKU "${skuCodigo}" no es de este cliente`;
          else if (cantidad === null || !Number.isFinite(cantidad) || cantidad <= 0)
            errorBase = 'Cantidad inválida';
          else if (kgRaw !== '' && (!Number.isFinite(cantidadKg!) || cantidadKg! < 0))
            errorBase = 'Kg inválido';
          out.push({
            idx: out.length,
            filaExcel: i + 1,
            fecha,
            skuCodigo,
            cantidad,
            cantidadKg,
            sku_id,
            candidatas,
            areaExcel,
            areaTexto,
            autoArea: candidatas.length === 1 ? candidatas[0]! : null,
            errorBase,
          });
        }
        setFilas(out);
        if (out.length === 0) toast.error('El archivo no tiene filas de datos');
      } catch {
        toast.error('No se pudo leer el archivo. ¿Es un .xlsx válido?');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  // Área efectiva por fila: la del Excel (manda) → la auto (única) → la elegida a mano.
  const areaEfectiva = (f: FilaParseada): string | null =>
    f.areaExcel ?? f.autoArea ?? picks[f.idx] ?? null;
  const pendientes = filas.filter((f) => !f.errorBase && areaEfectiva(f) === null);
  const errores = filas.filter((f) => f.errorBase);
  const cargables = filas.filter((f) => !f.errorBase && areaEfectiva(f) !== null);
  const puedeCargar = errores.length === 0 && pendientes.length === 0 && cargables.length > 0;

  // Preview agrupado por día (días de la semana primero; fechas inválidas al final).
  const grupos = (() => {
    const byFecha = new Map<string, FilaParseada[]>();
    for (const f of filas) {
      const arr = byFecha.get(f.fecha) ?? [];
      arr.push(f);
      byFecha.set(f.fecha, arr);
    }
    const orden = [...data.dias, ...[...byFecha.keys()].filter((k) => !data.dias.includes(k))];
    return orden
      .filter((fch) => byFecha.has(fch))
      .map((fch) => ({ fecha: fch, filas: byFecha.get(fch)! }));
  })();

  // Sanity check: total de la carga vs versión vigente de (cliente, planta).
  // Si la diferencia es drástica, el primer click muestra el aviso y el
  // segundo confirma la carga.
  const [avisoConfirmado, setAvisoConfirmado] = useState(false);
  useEffect(() => setAvisoConfirmado(false), [filas, picks, clienteId]);
  const aviso = useMemo(() => {
    if (!puedeCargar) return null;
    const totalNuevo = cargables.reduce((acc, f) => acc + (f.cantidad ?? 0), 0);
    const primeraArea = data.areas.find((a) => a.id === areaEfectiva(cargables[0]!));
    const vigente = vigenteComparable(
      data.programaciones,
      clienteId,
      primeraArea?.planta_id ?? null,
    );
    return vigente ? avisoTotalSospechoso(totalNuevo, vigente.total_paquetes) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeCargar, filas, picks, clienteId, data.areas, data.programaciones]);

  async function cargar() {
    if (!puedeCargar) return;
    if (aviso && !avisoConfirmado) {
      setAvisoConfirmado(true);
      return;
    }
    try {
      const res = await crear.mutateAsync({
        cliente_id: clienteId,
        semana_iso: data.semana_iso,
        origen: 'excel_alpina',
        detalles: cargables.map((f) => ({
          fecha: f.fecha,
          area_id: areaEfectiva(f)!,
          sku_id: f.sku_id!,
          cantidad_paquetes: f.cantidad!,
          cantidad_kg: f.cantidadKg,
        })),
      });
      toast.success(`Programación v${res.version} cargada (${cargables.length} filas)`);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error al cargar';
      toast.error('No se pudo cargar', { description: msg });
    }
  }

  return (
    <Dialog open onClose={onClose} className="max-w-4xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Importar programación desde Excel · {data.semana_iso}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="imp-cliente">Cliente</Label>
              <Select
                id="imp-cliente"
                value={clienteId}
                onChange={(e) => {
                  setClienteId(e.target.value);
                  setFilas([]);
                  setPicks({});
                }}
              >
                {clientesOrdenados.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </Select>
            </div>
            <Button variant="outline" type="button" onClick={descargarPlantilla}>
              Descargar plantilla
            </Button>
            <Button variant="outline" type="button" onClick={() => inputRef.current?.click()}>
              Subir archivo
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={onArchivo}
            />
            {nombreArchivo ? (
              <span className="text-xs text-fg-tertiary">{nombreArchivo}</span>
            ) : null}
          </div>

          {filas.length > 0 ? (
            <div className="flex flex-col gap-3">
              {/* Tira de resumen */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { label: 'Listas', value: cargables.length.toLocaleString('es-CO') },
                  {
                    label: 'Áreas',
                    value: new Set(cargables.map((f) => areaEfectiva(f))).size.toLocaleString(
                      'es-CO',
                    ),
                  },
                  {
                    label: 'Días',
                    value: new Set(cargables.map((f) => f.fecha)).size.toLocaleString('es-CO'),
                  },
                  {
                    label: 'Paquetes',
                    value: cargables
                      .reduce((a, f) => a + (f.cantidad ?? 0), 0)
                      .toLocaleString('es-CO'),
                  },
                ].map((k) => (
                  <div
                    key={k.label}
                    className="rounded border border-border bg-surface-overlay/40 px-3 py-2"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                      {k.label}
                    </div>
                    <div className="mt-0.5 text-base font-semibold tabular-nums text-fg">
                      {k.value}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-sm">
                {cargables.length} lista(s)
                {pendientes.length > 0 ? (
                  <span className="text-warn"> · {pendientes.length} sin área (elija abajo)</span>
                ) : null}
                {errores.length > 0 ? (
                  <span className="text-crit"> · {errores.length} con error</span>
                ) : null}
              </p>

              <div className="max-h-80 overflow-auto rounded border border-border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-surface-overlay text-fg-tertiary">
                    <tr>
                      <th className="px-2 py-1 text-left">Fila</th>
                      <th className="px-2 py-1 text-left">SKU</th>
                      <th className="px-2 py-1 text-right">Cant.</th>
                      <th className="px-2 py-1 text-left">Área</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupos.map((g) => {
                      const totalDia = g.filas.reduce((a, f) => a + (f.cantidad ?? 0), 0);
                      return (
                        <Fragment key={g.fecha}>
                          <tr className="border-t-2 border-border-strong bg-surface-overlay/60">
                            <td colSpan={2} className="px-2 py-1 font-medium text-fg-secondary">
                              {fmtDiaCorto(g.fecha)}
                            </td>
                            <td className="px-2 py-1 text-right tabular-nums text-fg-secondary">
                              {totalDia.toLocaleString('es-CO')}
                            </td>
                            <td className="px-2 py-1 text-fg-tertiary">{g.filas.length} fila(s)</td>
                          </tr>
                          {g.filas.map((f) => (
                            <tr key={f.idx} className="border-t border-border">
                              <td className="px-2 py-1 align-top text-fg-tertiary">
                                {f.filaExcel}
                              </td>
                              <td className="px-2 py-1 align-top">
                                <div className="font-medium">{f.skuCodigo}</div>
                                {skuDesc.get(norm(f.skuCodigo)) ? (
                                  <div className="text-fg-tertiary">
                                    {skuDesc.get(norm(f.skuCodigo))}
                                  </div>
                                ) : null}
                              </td>
                              <td className="px-2 py-1 text-right align-top tabular-nums">
                                {f.cantidad ?? '—'}
                              </td>
                              <td className="px-2 py-1 align-top">
                                {f.errorBase ? (
                                  <span className="text-crit">{f.errorBase}</span>
                                ) : f.areaExcel ? (
                                  <span className="text-ok">
                                    {areaLabel.get(f.areaExcel)}{' '}
                                    <span className="text-fg-tertiary">· del Excel</span>
                                  </span>
                                ) : f.autoArea ? (
                                  <span className="text-ok">{areaLabel.get(f.autoArea)}</span>
                                ) : (
                                  // Excel sin área (o no reconocida) y SKU ambiguo/sin config.
                                  <div className="flex flex-col gap-0.5">
                                    {f.areaTexto ? (
                                      <span className="text-warn">
                                        “{f.areaTexto}” no reconocida
                                      </span>
                                    ) : null}
                                    <Select
                                      value={picks[f.idx] ?? ''}
                                      onChange={(e) =>
                                        setPicks((p) => ({ ...p, [f.idx]: e.target.value }))
                                      }
                                      className="h-7 text-xs"
                                    >
                                      <option value="">
                                        {f.candidatas.length === 0
                                          ? 'Elija un área (SKU sin área configurada)'
                                          : 'Elija un área'}
                                      </option>
                                      {(f.candidatas.length > 0
                                        ? f.candidatas
                                        : data.areas.map((a) => a.id)
                                      ).map((aid) => (
                                        <option key={aid} value={aid}>
                                          {areaLabel.get(aid)}
                                        </option>
                                      ))}
                                    </Select>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {pendientes.length > 0 ? (
                <p className="text-xs text-warn">
                  Hay filas sin área resuelta. Elija el área en cada una para poder cargar. (Tip:
                  ponga el área en la columna “Área (opcional)” del Excel —nombre completo o código—
                  y se asigna sola.)
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-fg-tertiary">
              Elija el cliente, descargue la plantilla (Fecha · SKU ·{' '}
              <strong>Área (opcional)</strong> · Cantidad), complétela y súbala. Si pone el área
              (nombre completo o código) se asigna sola; si la dejás vacía, el sistema la deriva del
              SKU y, si hay duda, la elegís acá. Se crea una nueva versión.
            </p>
          )}
        </div>

        {aviso && avisoConfirmado ? (
          <p className="rounded border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
            ⚠ {aviso}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={cargar} disabled={!puedeCargar || crear.isPending}>
            {crear.isPending
              ? 'Cargando…'
              : aviso && avisoConfirmado
                ? 'Cargar de todos modos'
                : `Cargar ${cargables.length} fila(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
